import { type RngFn, boxMullerNormal, createRng } from '../shared/rng.util';
import type { CorrelationGeneratorConfig, RelationshipType } from './correlation-generator.model';

export interface CorrelatedPair {
  readonly xValues: number[];
  readonly yValues: number[];
}

// =============================================================================
// Linear correlation — exact sample Pearson r via Gram-Schmidt
// =============================================================================

/**
 * Generate linearly correlated data with exact sample Pearson r.
 *
 * Uses Gram-Schmidt orthogonalization:
 * 1. Generate two random normal vectors Z1, Z2
 * 2. Center both, orthogonalize Z2 w.r.t. Z1, standardize both
 * 3. Compose Y = r·X + sqrt(1−r²)·Z_orth
 *
 * This guarantees sample cor(X, Y) = targetR exactly.
 */
function generateLinear(n: number, targetR: number, rng: RngFn): CorrelatedPair {
  const z1: number[] = [];
  const z2: number[] = [];
  for (let i = 0; i < n; i++) {
    z1.push(boxMullerNormal(0, 1, rng));
    z2.push(boxMullerNormal(0, 1, rng));
  }

  // Center both to mean 0
  let m1 = 0,
    m2 = 0;
  for (let i = 0; i < n; i++) {
    m1 += z1[i];
    m2 += z2[i];
  }
  m1 /= n;
  m2 /= n;
  for (let i = 0; i < n; i++) {
    z1[i] -= m1;
    z2[i] -= m2;
  }

  // Orthogonalize z2 w.r.t. z1 (Gram-Schmidt)
  let dot12 = 0,
    dot11 = 0;
  for (let i = 0; i < n; i++) {
    dot12 += z1[i] * z2[i];
    dot11 += z1[i] * z1[i];
  }
  if (dot11 > 1e-12) {
    const proj = dot12 / dot11;
    for (let i = 0; i < n; i++) {
      z2[i] -= proj * z1[i];
    }
  }

  // Standardize both to unit sample variance
  let v1 = 0,
    v2 = 0;
  for (let i = 0; i < n; i++) {
    v1 += z1[i] * z1[i];
    v2 += z2[i] * z2[i];
  }
  const sd1 = Math.sqrt(v1 / n);
  const sd2 = Math.sqrt(v2 / n);
  if (sd1 < 1e-12 || sd2 < 1e-12) {
    return { xValues: z1, yValues: z2 };
  }
  for (let i = 0; i < n; i++) {
    z1[i] /= sd1;
    z2[i] /= sd2;
  }

  // Y = r·X + sqrt(1−r²)·Z_orth → exact sample Pearson r
  const complement = Math.sqrt(Math.max(0, 1 - targetR * targetR));
  const xValues: number[] = [];
  const yValues: number[] = [];
  for (let i = 0; i < n; i++) {
    xValues.push(z1[i]);
    yValues.push(targetR * z1[i] + complement * z2[i]);
  }

  return { xValues, yValues };
}

// =============================================================================
// Non-linear correlation — exact sample R² via binary-searched noise
// =============================================================================

/**
 * Generate signal values for a given relationship type.
 * Returns X, signal(X), and independent unit noise.
 */
function generateSignalAndNoise(
  n: number,
  type: RelationshipType,
  direction: number,
  rng: RngFn,
): { xValues: number[]; signalValues: number[]; noiseValues: number[] } {
  const xValues: number[] = [];
  const signalValues: number[] = [];
  const noiseValues: number[] = [];

  for (let i = 0; i < n; i++) {
    let x: number;
    let signal: number;

    switch (type) {
      case 'quadratic':
        x = boxMullerNormal(0, 1, rng);
        signal = direction * x * x;
        break;
      case 'exponential':
        x = boxMullerNormal(3, 1, rng);
        signal = Math.exp(direction * 0.5 * x);
        break;
      default:
        x = boxMullerNormal(0, 1, rng);
        signal = 0;
    }

    xValues.push(x);
    signalValues.push(signal);
    noiseValues.push(boxMullerNormal(0, 1, rng));
  }

  return { xValues, signalValues, noiseValues };
}

/**
 * Generate non-linear relationship with exact sample R².
 *
 * Uses binary search on noise scale: Y = signal + k·noise.
 * When k=0, R²=1 (perfect fit). As k increases, R² decreases.
 * Finds k such that the model-specific R² matches the target.
 */
function generateNonLinear(
  n: number,
  targetRSquared: number,
  type: RelationshipType,
  direction: number,
  rng: RngFn,
): CorrelatedPair {
  const { xValues, signalValues, noiseValues } = generateSignalAndNoise(n, type, direction, rng);

  // Perfect fit → return signal directly
  if (targetRSquared >= 0.999) {
    return { xValues, yValues: [...signalValues] };
  }

  // Very low fit → return independent noise
  if (targetRSquared <= 0.001) {
    return { xValues, yValues: noiseValues };
  }

  // Binary search for noise scale k where R²(x, signal + k·noise) = target
  let lo = 0;
  let hi = 10;

  // Expand hi until R² at hi is below target
  for (let attempt = 0; attempt < 8; attempt++) {
    const yTest = signalValues.map((s, i) => s + hi * noiseValues[i]);
    const rSq = computeRSquared(xValues, yTest, type);
    if (rSq <= targetRSquared) {
      break;
    }
    hi *= 10;
  }

  let bestScale = (lo + hi) / 2;
  for (let iter = 0; iter < 60; iter++) {
    bestScale = (lo + hi) / 2;
    const yTest = signalValues.map((s, i) => s + bestScale * noiseValues[i]);
    const rSq = computeRSquared(xValues, yTest, type);

    if (Math.abs(rSq - targetRSquared) < 0.001) {
      break;
    }

    if (rSq > targetRSquared) {
      lo = bestScale; // Need more noise to reduce R²
    } else {
      hi = bestScale; // Need less noise to increase R²
    }
  }

  return {
    xValues,
    yValues: signalValues.map((s, i) => s + bestScale * noiseValues[i]),
  };
}

// =============================================================================
// Scaling & formatting
// =============================================================================

function scaleToRange(values: number[], min: number, max: number): number[] {
  const vMin = Math.min(...values);
  const vMax = Math.max(...values);
  const range = vMax - vMin;
  if (range === 0) {
    return values.map(() => (min + max) / 2);
  }
  return values.map((v) => min + ((v - vMin) / range) * (max - min));
}

function applyDecimalPlaces(values: number[], dp: number): number[] {
  return values.map((v) => Number(v.toFixed(dp)));
}

// =============================================================================
// Public API
// =============================================================================

export function generateCorrelatedData(config: CorrelationGeneratorConfig): CorrelatedPair {
  const { relationshipType, targetStrength, curveDirection, sampleSize, decimalPlaces, seed } = config;
  const rng = createRng(seed);
  const dir = curveDirection === 'positive' ? 1 : -1;

  let raw: CorrelatedPair;

  switch (relationshipType) {
    case 'linear':
      raw = generateLinear(sampleSize, targetStrength, rng);
      break;
    case 'quadratic':
    case 'exponential':
      raw = generateNonLinear(sampleSize, targetStrength, relationshipType, dir, rng);
      break;
  }

  // Scale to reasonable ranges
  let xValues = scaleToRange(raw.xValues, 0, 100);
  let yValues = scaleToRange(raw.yValues, 0, 100);

  xValues = applyDecimalPlaces(xValues, decimalPlaces);
  yValues = applyDecimalPlaces(yValues, decimalPlaces);

  return { xValues, yValues };
}

/**
 * Compute Pearson correlation coefficient for two arrays.
 */
export function pearsonR(x: readonly number[], y: readonly number[]): number {
  const n = Math.min(x.length, y.length);
  if (n < 2) {
    return 0;
  }

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  let sumY2 = 0;

  for (let i = 0; i < n; i++) {
    sumX += x[i];
    sumY += y[i];
    sumXY += x[i] * y[i];
    sumX2 += x[i] * x[i];
    sumY2 += y[i] * y[i];
  }

  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

  if (denominator === 0) {
    return 0;
  }

  return numerator / denominator;
}

/**
 * Compute R² = 1 − SS_res / SS_tot from actual and predicted values.
 */
function rSquaredFromPredicted(actual: readonly number[], predicted: readonly number[]): number {
  const n = actual.length;
  if (n < 2) {
    return 0;
  }

  let sumY = 0;
  for (let i = 0; i < n; i++) {
    sumY += actual[i];
  }
  const meanY = sumY / n;

  let ssTot = 0;
  let ssRes = 0;
  for (let i = 0; i < n; i++) {
    ssTot += (actual[i] - meanY) ** 2;
    ssRes += (actual[i] - predicted[i]) ** 2;
  }

  if (ssTot === 0) {
    return 1;
  }
  return Math.max(0, 1 - ssRes / ssTot);
}

/**
 * Fit a degree-2 polynomial y = ax² + bx + c using least squares (normal equations).
 * Returns predicted y values.
 */
function fitQuadratic(x: readonly number[], y: readonly number[]): number[] {
  const n = x.length;
  // Build sums for the 3×3 normal equation system
  let s0 = 0,
    s1 = 0,
    s2 = 0,
    s3 = 0,
    s4 = 0;
  let t0 = 0,
    t1 = 0,
    t2 = 0;

  for (let i = 0; i < n; i++) {
    const xi = x[i];
    const x2 = xi * xi;
    s0 += 1;
    s1 += xi;
    s2 += x2;
    s3 += x2 * xi;
    s4 += x2 * x2;
    t0 += y[i];
    t1 += xi * y[i];
    t2 += x2 * y[i];
  }

  // Solve [s4 s3 s2; s3 s2 s1; s2 s1 s0] · [a; b; c] = [t2; t1; t0]
  // Using Cramer's rule for 3×3
  const m = [
    [s4, s3, s2],
    [s3, s2, s1],
    [s2, s1, s0],
  ];
  const rhs = [t2, t1, t0];

  const det3 = (mat: number[][]): number =>
    mat[0][0] * (mat[1][1] * mat[2][2] - mat[1][2] * mat[2][1]) -
    mat[0][1] * (mat[1][0] * mat[2][2] - mat[1][2] * mat[2][0]) +
    mat[0][2] * (mat[1][0] * mat[2][1] - mat[1][1] * mat[2][0]);

  const D = det3(m);
  if (Math.abs(D) < 1e-12) {
    // Degenerate — return mean as prediction
    const mean = t0 / n;
    return x.map(() => mean);
  }

  const Da = det3([
    [rhs[0], m[0][1], m[0][2]],
    [rhs[1], m[1][1], m[1][2]],
    [rhs[2], m[2][1], m[2][2]],
  ]);
  const Db = det3([
    [m[0][0], rhs[0], m[0][2]],
    [m[1][0], rhs[1], m[1][2]],
    [m[2][0], rhs[2], m[2][2]],
  ]);
  const Dc = det3([
    [m[0][0], m[0][1], rhs[0]],
    [m[1][0], m[1][1], rhs[1]],
    [m[2][0], m[2][1], rhs[2]],
  ]);

  const a = Da / D;
  const b = Db / D;
  const c = Dc / D;

  return x.map((xi) => a * xi * xi + b * xi + c);
}

/**
 * Fit exponential y = a·e^(bx) via linear regression on ln(y) = ln(a) + bx.
 * Returns predicted y values.
 */
function fitExponential(x: readonly number[], y: readonly number[]): number[] {
  const n = x.length;
  const lnY: number[] = [];
  const xf: number[] = [];

  // Filter out non-positive y values (can't take log)
  for (let i = 0; i < n; i++) {
    if (y[i] > 0) {
      xf.push(x[i]);
      lnY.push(Math.log(y[i]));
    }
  }

  if (xf.length < 2) {
    const mean = y.reduce((a, b) => a + b, 0) / n;
    return x.map(() => mean);
  }

  // Linear regression on (xf, lnY)
  let sumX = 0,
    sumLnY = 0,
    sumX2 = 0,
    sumXLnY = 0;
  const nf = xf.length;
  for (let i = 0; i < nf; i++) {
    sumX += xf[i];
    sumLnY += lnY[i];
    sumX2 += xf[i] * xf[i];
    sumXLnY += xf[i] * lnY[i];
  }

  const denom = nf * sumX2 - sumX * sumX;
  if (Math.abs(denom) < 1e-12) {
    const mean = y.reduce((a, b) => a + b, 0) / n;
    return x.map(() => mean);
  }

  const bCoef = (nf * sumXLnY - sumX * sumLnY) / denom;
  const lnA = (sumLnY - bCoef * sumX) / nf;
  const aCoef = Math.exp(lnA);

  return x.map((xi) => aCoef * Math.exp(bCoef * xi));
}

/**
 * Compute model-specific R² (coefficient of determination).
 *
 * - linear: Pearson r²
 * - quadratic: polynomial fit R²
 * - exponential: exponential fit R²
 */
export function computeRSquared(
  x: readonly number[],
  y: readonly number[],
  type: import('./correlation-generator.model').RelationshipType = 'linear',
): number {
  const n = Math.min(x.length, y.length);
  if (n < 2) {
    return 0;
  }

  switch (type) {
    case 'linear': {
      const r = pearsonR(x, y);
      return r * r;
    }
    case 'quadratic': {
      const predicted = fitQuadratic(x, y);
      return rSquaredFromPredicted(y, predicted);
    }
    case 'exponential': {
      const predicted = fitExponential(x, y);
      return rSquaredFromPredicted(y, predicted);
    }
  }
}
